import { useEffect, useRef, useState } from "react"
import { gsap } from "gsap"
import { useNavigate } from "react-router-dom"
import { CourseCard } from "./CourseCard"
import { cursosAPI } from "../services/api"

export function CourseGrid() {
  const gridRef = useRef(null)
  const navigate = useNavigate()
  const [cursos, setCursos] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingMatricula, setLoadingMatricula] = useState({})

  useEffect(() => {
    carregarCursos()
  }, [])

  useEffect(() => {
    if (!loading) {
      const ctx = gsap.context(() => {
        // Verificar e animar títulos das seções
        const sectionTitles = gridRef.current?.querySelectorAll(".section-title")
        if (sectionTitles && sectionTitles.length > 0) {
          sectionTitles.forEach((title, index) => {
            gsap.fromTo(
              title,
              { opacity: 0, x: -30 },
              {
                opacity: 1,
                x: 0,
                duration: 0.6,
                delay: index * 0.1,
                ease: "power3.out",
                scrollTrigger: {
                  trigger: title,
                  start: "top 90%",
                  end: "bottom 10%",
                  toggleActions: "play none none reverse",
                },
              },
            )
          })
        }

        // Verificar e animar seções
        const courseSections = gridRef.current?.querySelectorAll(".course-section")
        if (courseSections && courseSections.length > 0) {
          courseSections.forEach((section, index) => {
            gsap.fromTo(
              section,
              { opacity: 0, y: 60 },
              {
                opacity: 1,
                y: 0,
                duration: 0.8,
                delay: index * 0.2,
                ease: "power3.out",
                scrollTrigger: {
                  trigger: section,
                  start: "top 85%",
                  end: "bottom 15%",
                  toggleActions: "play none none reverse",
                },
              },
            )
          })
        }
      }, gridRef)

      return () => ctx.revert()
    }
  }, [loading])

  const carregarCursos = async () => {
    try {
      setLoading(true)
      const response = await cursosAPI.listar()
      setCursos(response.data || [])
    } catch (err) {
      console.error('Erro ao carregar cursos:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleMatricula = async (cursoId, isMatriculado) => {
    try {
      setLoadingMatricula(prev => ({ ...prev, [cursoId]: true }))
      
      if (isMatriculado) {
        await cursosAPI.desmatricular(cursoId)
      } else {
        await cursosAPI.matricular(cursoId)
      }
      
      // Recarregar cursos para atualizar estado
      await carregarCursos()
    } catch (err) {
      console.error('Erro na matrícula:', err)
      alert(err.response?.data?.erro || 'Erro ao realizar operação')
    } finally {
      setLoadingMatricula(prev => ({ ...prev, [cursoId]: false }))
    }
  }

  const handleVerDetalhes = (cursoId) => {
    navigate(`/curso/${cursoId}`)
  }

  const handleGerarCertificado = async (cursoId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('http://localhost:5000/api/certificados/gerar', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ curso_id: cursoId })
      });

      const data = await response.json();

      if (response.ok) {
        alert(`Certificado gerado com sucesso! Credencial: ${data.credential_id}`);
        
        if (data.pdf_url) {
          window.open(`http://localhost:5000${data.pdf_url}`, '_blank');
        }

        // Recarregar cursos para atualizar o estado dos botões
        await carregarCursos();
      } else {
        throw new Error(data.erro || 'Erro ao gerar certificado');
      }
    } catch (error) {
      console.error('Erro ao gerar certificado:', error);
      alert(error.message || 'Erro ao gerar certificado');
    }
  };

  const handleVerCertificado = (certificado) => {
    if (certificado?.pdf_path) {
      window.open(`http://localhost:5000/uploads/${certificado.pdf_path}`, '_blank');
    } else if (certificado?.credential_id) {
      window.open(`/certificados/${certificado.credential_id}`, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen py-16 px-6 lg:px-8 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <div className="container mx-auto">
          <div className="flex flex-col items-center justify-center h-96 space-y-8">
            {/* Loading spinner melhorado */}
            <div className="relative">
              <div className="w-20 h-20 border-4 border-slate-700 border-t-red-600 rounded-full animate-spin"></div>
              <div className="absolute inset-0 w-20 h-20 border-4 border-transparent border-l-red-500 rounded-full animate-spin animation-delay-150"></div>
            </div>
            
            <div className="text-center space-y-3">
              <h3 className="text-2xl font-bold text-white">Carregando cursos...</h3>
              <p className="text-slate-400 text-lg">Preparando o melhor conteúdo para você</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Organizar cursos por categorias
  const cursosMatriculados = cursos.filter(curso => curso.matriculado)
  const cursosComProgresso = cursosMatriculados.filter(curso => curso.progresso && curso.progresso.porcentagem > 0)
  const cursosSemProgresso = cursos.filter(curso => !curso.matriculado)
  const cursosPopulares = cursos.filter(curso => curso.total_alunos > 0).sort((a, b) => (b.total_alunos || 0) - (a.total_alunos || 0))

  const categorias = []

  // Continuar Assistindo (cursos matriculados com progresso)
  if (cursosComProgresso.length > 0) {
    categorias.push({
      title: "Continuar Assistindo",
      subtitle: "Retome seus estudos de onde parou",
      courses: cursosComProgresso,
      icon: "▶️"
    })
  }

  // Meus Cursos (todos os matriculados)
  if (cursosMatriculados.length > 0) {
    categorias.push({
      title: "Meus Cursos",
      subtitle: `${cursosMatriculados.length} curso${cursosMatriculados.length !== 1 ? 's' : ''} na sua biblioteca`,
      courses: cursosMatriculados,
      icon: "📚"
    })
  }

  // Cursos Populares
  if (cursosPopulares.length > 0) {
    categorias.push({
      title: "Cursos Populares",
      subtitle: "Os mais assistidos pela comunidade",
      courses: cursosPopulares.slice(0, 8),
      icon: "🔥"
    })
  }

  // Todos os Cursos
  if (cursos.length > 0) {
    categorias.push({
      title: "Explore Todos os Cursos",
      subtitle: `Descubra ${cursos.length} curso${cursos.length !== 1 ? 's' : ''} disponível${cursos.length !== 1 ? 'is' : ''}`,
      courses: cursos,
      icon: "🎯"
    })
  }

  if (categorias.length === 0) {
    return (
      <div className="min-h-screen py-16 px-6 lg:px-8 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <div className="container mx-auto">
          <div className="text-center py-24 space-y-6">
            <div className="text-6xl mb-8">📚</div>
            <h3 className="text-4xl font-bold text-white mb-4">Nenhum curso disponível</h3>
            <p className="text-xl text-slate-400 max-w-md mx-auto leading-relaxed">
              Novos cursos incríveis estão sendo preparados para você. Volte em breve!
            </p>
            <div className="mt-8">
              <div className="inline-flex items-center gap-2 px-6 py-3 bg-red-600/20 text-red-400 rounded-full border border-red-600/30">
                <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                Em breve novidades
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div ref={gridRef} className="min-h-screen py-16 px-6 lg:px-8 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <div className="container mx-auto space-y-16">
        {categorias.map((categoria, categoryIndex) => (
          <section key={categoria.title} className="course-section">
            {/* Cabeçalho da seção */}
            <div className="section-title mb-8 pb-6 border-b border-slate-800/50">
              <div className="flex items-center gap-4 mb-3">
                <span className="text-3xl">{categoria.icon}</span>
                <h2 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent">
                  {categoria.title}
                </h2>
              </div>
              <p className="text-slate-400 text-lg ml-16">{categoria.subtitle}</p>
            </div>

            {/* Grid de cursos */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {categoria.courses.map((course, courseIndex) => (
                <CourseCard 
                  key={course.id} 
                  course={course} 
                  delay={courseIndex * 0.1}
                  onMatricula={handleMatricula}
                  onVerDetalhes={handleVerDetalhes}
                  onGerarCertificado={handleGerarCertificado}
                  onVerCertificado={handleVerCertificado}
                  loadingMatricula={loadingMatricula[course.id]}
                />
              ))}
            </div>

            {/* Linha divisória entre seções (exceto na última) */}
            {categoryIndex < categorias.length - 1 && (
              <div className="mt-16 flex items-center justify-center">
                <div className="h-px bg-gradient-to-r from-transparent via-slate-700 to-transparent w-full max-w-xs"></div>
              </div>
            )}
          </section>
        ))}

        {/* Footer informativo */}
        <div className="text-center py-12 space-y-4">
          <p className="text-slate-500 text-sm">
            Explore nossa biblioteca com mais de {cursos.length} cursos especializados
          </p>
          <div className="flex items-center justify-center gap-6 text-xs text-slate-600">
            <span>✨ Atualizado semanalmente</span>
            <span>🎓 Certificados inclusos</span>
            <span>📱 Acesso mobile</span>
          </div>
        </div>
      </div>
    </div>
  )
} 